import { useState } from "react";
import { Plus, X } from "lucide-react";

const INPUT =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition";

export function Field({ label, hint, optional, sensitive, error, children }) {
  return (
    <div className="space-y-1.5" data-testid={`field-${slug(label)}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="text-sm font-medium text-slate-700">
          {label}
          {optional && !sensitive ? (
            <span className="ml-1.5 text-xs font-normal text-slate-400">
              (optional)
            </span>
          ) : null}
        </label>
        {sensitive ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-100 px-2.5 py-0.5 text-[11px] font-medium text-amber-800"
            data-testid={`sensitive-tag-${slug(label)}`}
          >
            Sensitive · optional
          </span>
        ) : null}
      </div>
      {children}
      {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
      {error ? (
        <p className="text-xs text-red-600" data-testid={`error-${slug(label)}`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

function slug(s = "") {
  return String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function TextInput({ value, onChange, placeholder, type = "text", testid, ...rest }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
      placeholder={placeholder}
      className={INPUT}
      data-testid={testid}
      {...rest}
    />
  );
}

export function NumberInput({ value, onChange, placeholder, min, max, step, testid }) {
  return (
    <input
      type="number"
      value={value ?? ""}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === "") return onChange(null);
        const num = Number(raw);
        onChange(Number.isNaN(num) ? null : num);
      }}
      placeholder={placeholder}
      min={min}
      max={max}
      step={step}
      className={INPUT}
      data-testid={testid}
    />
  );
}

export function TextArea({ value, onChange, placeholder, rows = 4, testid }) {
  return (
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className={INPUT + " resize-y"}
      data-testid={testid}
    />
  );
}

export function SelectInput({ value, onChange, options, placeholder = "Select…", testid }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
      className={INPUT + " appearance-none bg-no-repeat pr-10"}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2364748b'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 011.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z' clip-rule='evenodd'/%3E%3C/svg%3E\")",
        backgroundPosition: "right 0.75rem center",
        backgroundSize: "1.1em",
      }}
      data-testid={testid}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) =>
        typeof opt === "string" ? (
          <option key={opt} value={opt}>
            {labelize(opt)}
          </option>
        ) : (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        )
      )}
    </select>
  );
}

function labelize(s) {
  return String(s)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function TriToggle({ value, onChange, testid }) {
  // value: true | false | null
  const opts = [
    { v: true, label: "Yes" },
    { v: false, label: "No" },
    { v: null, label: "Prefer not to say" },
  ];
  return (
    <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1" data-testid={testid}>
      {opts.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={String(o.v)}
            type="button"
            onClick={() => onChange(o.v)}
            className={
              "px-3.5 py-1.5 text-sm rounded-lg transition-colors " +
              (active
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:text-slate-900")
            }
            data-testid={`${testid}-${o.v === null ? "skip" : o.v ? "yes" : "no"}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function TagInput({ value, onChange, placeholder = "Add and press Enter", testid }) {
  const [draft, setDraft] = useState("");
  const list = Array.isArray(value) ? value : [];

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (list.includes(v)) {
      setDraft("");
      return;
    }
    onChange([...list, v]);
    setDraft("");
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-2 mb-2">
        {list.map((tag, i) => (
          <span
            key={`${tag}-${i}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 border border-sky-100 px-3 py-1 text-sm text-sky-800"
            data-testid={`${testid}-tag-${i}`}
          >
            {tag}
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-sky-700 hover:text-sky-900"
              aria-label={`Remove ${tag}`}
              data-testid={`${testid}-tag-${i}-remove`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className={INPUT}
          data-testid={`${testid}-draft`}
        />
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
          data-testid={`${testid}-add`}
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>
    </div>
  );
}

export function CheckGroup({ value, onChange, options, testid }) {
  const list = Array.isArray(value) ? value : [];
  const toggle = (v) => {
    if (list.includes(v)) onChange(list.filter((x) => x !== v));
    else onChange([...list, v]);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => {
        const v = typeof opt === "string" ? opt : opt.value;
        const label = typeof opt === "string" ? labelize(opt) : opt.label;
        const active = list.includes(v);
        return (
          <button
            key={v}
            type="button"
            onClick={() => toggle(v)}
            className={
              "px-3.5 py-1.5 text-sm rounded-full border transition-all " +
              (active
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-300")
            }
            data-testid={`${testid}-${v}`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function ListEditor({ items, onChange, addLabel, newItem, renderRow, testid }) {
  const list = Array.isArray(items) ? items : [];

  // Ensure every row has a stable client-side id so React's reconciliation
  // is correct when rows are removed/reordered. We add `_rid` on insert and
  // strip it on the way out via the persistence layer (server ignores it).
  const ensureId = (it) =>
    it && it._rid
      ? it
      : { ...it, _rid: `r_${Math.random().toString(36).slice(2, 10)}` };

  const add = () => onChange([...list, ensureId(newItem())]);
  const update = (idx, patch) =>
    onChange(list.map((it, i) => (i === idx ? ensureId({ ...it, ...patch }) : it)));
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3" data-testid={testid}>
      {list.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No entries yet.</p>
      ) : null}
      {list.map((item, i) => {
        const stableKey = item?._rid || `idx-${i}`;
        return (
          <div
            key={stableKey}
            className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 relative"
            data-testid={`${testid}-row-${i}`}
          >
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute top-3 right-3 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-200"
              aria-label="Remove entry"
              data-testid={`${testid}-row-${i}-remove`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
            {renderRow(item, (patch) => update(i, patch), i)}
          </div>
        );
      })}
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400"
        data-testid={`${testid}-add`}
      >
        <Plus className="h-4 w-4" />
        {addLabel}
      </button>
    </div>
  );
}
